import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Bed, Bath, Maximize2, Car, Compass, ArrowLeft, Phone, Mail, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchProperty, submitEnquiry } from "@/lib/api";
import { COMPANY, WHATSAPP_URL } from "@/lib/constants";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProperty(id).then((d) => setP(d)).catch(() => setP(null));
    window.scrollTo(0, 0);
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, property_id: id });
      toast.success("Enquiry sent! Our team will reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setSubmitting(false);
  };

  if (!p) return <div className="pt-32 min-h-screen text-center text-brand-navy">Loading property...</div>;

  return (
    <div className="pt-28 pb-24 bg-brand-ivory">
      <SEO title={p.title} description={`${p.title} in ${p.location || p.city}. ${p.price_label}. ${p.description?.slice(0, 140) || ''}`} image={p.images?.[0]} path={`/properties/${p.id}`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Link to="/properties" className="text-sm text-brand-navy hover:text-brand-gold inline-flex items-center gap-2 mb-6"><ArrowLeft size={14} /> Back to listings</Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Gallery + details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="aspect-[16/10] bg-white overflow-hidden border border-brand-gold/20">
              <img src={p.images?.[activeImg] || p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
            </div>
            {p.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto">
                {p.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`flex-shrink-0 w-24 h-20 border-2 ${i === activeImg ? "border-brand-gold" : "border-transparent"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="bg-white border border-brand-gold/20 p-6 lg:p-8">
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div>
                  <div className="text-brand-gold font-serif text-4xl">{p.price_label}</div>
                  <h1 className="mt-2 font-serif text-3xl text-brand-navy">{p.title}</h1>
                  <div className="mt-1 flex items-center gap-1 text-sm text-brand-charcoal/70"><MapPin size={14} className="text-brand-gold" /> {p.location}, {p.state}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {(p.badges || []).map((b) => <span key={b} className="bg-brand-gold text-brand-navy text-[10px] font-bold px-2 py-1 uppercase tracking-widest">{b}</span>)}
                  <span className="bg-brand-navy text-white text-[10px] font-bold px-2 py-1 uppercase tracking-widest">For {p.purpose}</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-brand-gold/20 pt-6">
                <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Area</div><div className="font-serif text-lg text-brand-navy flex items-center gap-1"><Maximize2 size={14} className="text-brand-gold" /> {p.area} {p.area_unit}</div></div>
                {p.bhk && <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Configuration</div><div className="font-serif text-lg text-brand-navy flex items-center gap-1"><Bed size={14} className="text-brand-gold" /> {p.bhk} BHK</div></div>}
                {p.bathrooms && <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Bathrooms</div><div className="font-serif text-lg text-brand-navy flex items-center gap-1"><Bath size={14} className="text-brand-gold" /> {p.bathrooms}</div></div>}
                {p.parking && <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Parking</div><div className="font-serif text-lg text-brand-navy flex items-center gap-1"><Car size={14} className="text-brand-gold" /> {p.parking}</div></div>}
                {p.facing && <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Facing</div><div className="font-serif text-lg text-brand-navy flex items-center gap-1"><Compass size={14} className="text-brand-gold" /> {p.facing}</div></div>}
                {p.furnishing && <div><div className="text-[10px] tracking-widest uppercase text-brand-charcoal/60">Furnishing</div><div className="font-serif text-lg text-brand-navy">{p.furnishing}</div></div>}
              </div>

              {p.description && (
                <div className="mt-6 border-t border-brand-gold/20 pt-6">
                  <h3 className="font-serif text-xl text-brand-navy mb-2">About this property</h3>
                  <p className="text-sm text-brand-charcoal/80 leading-relaxed">{p.description}</p>
                </div>
              )}

              {p.amenities?.length > 0 && (
                <div className="mt-6 border-t border-brand-gold/20 pt-6">
                  <h3 className="font-serif text-xl text-brand-navy mb-3">Amenities</h3>
                  <div className="flex flex-wrap gap-2">
                    {p.amenities.map((a) => <span key={a} className="border border-brand-gold/40 text-sm px-3 py-1 text-brand-navy">{a}</span>)}
                  </div>
                </div>
              )}

              <div className="mt-6 border-t border-brand-gold/20 pt-6">
                <h3 className="font-serif text-xl text-brand-navy mb-3">Location</h3>
                <div className="aspect-video">
                  <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent((p.location || '') + ', ' + (p.city || '') + ', ' + (p.state || ''))}&output=embed`} className="w-full h-full border-0" loading="lazy" />
                </div>
              </div>
            </div>
          </div>

          {/* Enquiry */}
          <aside className="lg:col-span-1">
            <div className="bg-brand-navy text-white p-6 sticky top-28">
              <div className="divider-gold">Interested?</div>
              <h3 className="mt-3 font-serif text-2xl">Get in Touch</h3>
              <p className="text-sm text-brand-ivory/70 mt-1">Our advisor will call you within 2 hours.</p>
              <form onSubmit={submit} className="mt-5 space-y-3">
                <Input required data-testid="detail-enq-name" placeholder="Your Name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm bg-white/10 border-brand-gold/40 text-white placeholder:text-white/60" />
                <Input required data-testid="detail-enq-phone" placeholder="Phone Number*" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm bg-white/10 border-brand-gold/40 text-white placeholder:text-white/60" />
                <Input data-testid="detail-enq-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm bg-white/10 border-brand-gold/40 text-white placeholder:text-white/60" />
                <Textarea data-testid="detail-enq-message" placeholder="Message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm bg-white/10 border-brand-gold/40 text-white placeholder:text-white/60" />
                <Button type="submit" disabled={submitting} data-testid="detail-enq-submit" className="w-full btn-gold rounded-sm h-11">{submitting ? "Sending..." : "Send Enquiry"}</Button>
              </form>
              <div className="mt-6 space-y-2 text-sm">
                <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:text-brand-gold"><Phone size={14} className="text-brand-gold" /> {COMPANY.phone}</a>
                <a href={WHATSAPP_URL(`Hi, I'm interested in "${p.title}"`)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-brand-gold"><MessageCircle size={14} className="text-brand-gold" /> Chat on WhatsApp</a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-brand-gold"><Mail size={14} className="text-brand-gold" /> {COMPANY.email}</a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
